import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  CellValueChangedEvent,
  ColDef,
  GetContextMenuItems,
  GridReadyEvent,
  PaginationChangedEvent,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';

import { useIsMobile } from '@/app/hooks/useIsMobile';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';

const MOBILE_PAGE_SIZE = 10;

const MobilePagination = ({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) => {
  const startItem = (currentPage - 1) * MOBILE_PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * MOBILE_PAGE_SIZE, totalItems);

  return (
    <div className="mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-sm text-gray-600">
        {startItem}-{endItem} of {totalItems}
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="pi pi-angle-double-left text-sm" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="pi pi-angle-left text-sm" />
        </button>
        <span className="px-3 text-sm font-medium">
          {currentPage} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="pi pi-angle-right text-sm" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="rounded p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="pi pi-angle-double-right text-sm" />
        </button>
      </div>
    </div>
  );
};

export type ResponsiveDataViewProps<T> = {
  rowData: T[];
  columnDefs: ColDef[];
  storageKey: string;
  getRowId: (data: T) => string;
  renderCard: (item: T) => ReactNode;
  emptyMessage?: string;
  gridHeight?: string;

  // AG Grid options
  context?: any;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationPageSizeSelector?: number[];
  rowSelection?: 'single' | 'multiple';
  suppressRowClickSelection?: boolean;
  onRowDoubleClicked?: (event: RowDoubleClickedEvent) => void;
  onPaginationChanged?: (event: PaginationChangedEvent) => void;
  onSelectionChanged?: (event: SelectionChangedEvent) => void;
  onGridReady?: (event: GridReadyEvent) => void;
  onCellValueChanged?: (event: CellValueChangedEvent) => void;
  getContextMenuItems?: GetContextMenuItems;
  gridRef?: React.RefObject<AgGridReact>;

  // Mobile card tap action
  onCardClick?: (item: T) => void;
};

export function ResponsiveDataView<T>({
  rowData,
  columnDefs,
  storageKey,
  getRowId,
  renderCard,
  emptyMessage = 'No data found',
  gridHeight = '600px',
  context,
  pagination = true,
  paginationPageSize = 100,
  paginationPageSizeSelector = [20, 50, 100, 250, 500, 1000],
  rowSelection,
  suppressRowClickSelection,
  onRowDoubleClicked,
  onPaginationChanged,
  onSelectionChanged,
  onGridReady,
  onCellValueChanged,
  getContextMenuItems,
  gridRef: externalGridRef,
  onCardClick,
}: ResponsiveDataViewProps<T>) {
  const internalGridRef = useRef<AgGridReact>(null);
  const gridRef = externalGridRef || internalGridRef;
  const defaultGridOptions = useDefaultGridOptions();
  const isMobile = useIsMobile();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(rowData.length / MOBILE_PAGE_SIZE));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * MOBILE_PAGE_SIZE;
    return rowData.slice(start, start + MOBILE_PAGE_SIZE);
  }, [rowData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowData]);

  if (isMobile) {
    return (
      <div className="flex flex-col">
        {rowData.length === 0 ? (
          <div className="py-8 text-center text-gray-500">{emptyMessage}</div>
        ) : (
          <>
            <MobilePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={rowData.length}
              onPageChange={setCurrentPage}
            />
            <div className="flex flex-col gap-3">
              {paginatedData.map((item) => (
                <div
                  key={getRowId(item)}
                  onClick={onCardClick ? () => onCardClick(item) : undefined}
                  className={onCardClick ? 'cursor-pointer' : ''}
                >
                  {renderCard(item)}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ height: gridHeight }}>
      <WithLocalStorage storageKey={storageKey}>
        <AgGridReact
          gridOptions={defaultGridOptions}
          ref={gridRef}
          className="ag-theme-transova"
          defaultColDef={defaultColDef}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          overlayNoRowsTemplate={emptyMessage}
          getRowId={(params) => getRowId(params.data)}
          onRowDoubleClicked={onRowDoubleClicked}
          onPaginationChanged={onPaginationChanged}
          onSelectionChanged={onSelectionChanged}
          onGridReady={onGridReady}
          onCellValueChanged={onCellValueChanged}
          getContextMenuItems={getContextMenuItems}
          context={context}
          rowSelection={rowSelection}
          suppressRowClickSelection={suppressRowClickSelection}
        />
      </WithLocalStorage>
    </div>
  );
}
